import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readCache, writeCache } from '../../../services/localCache';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Plus, RotateCcw, Trash2, PartyPopper, Users, HandCoins, X, AlertTriangle, Ban } from 'lucide-react';
import { getDebts, patchDebtSettled, deleteDebt, createBulkDebt, BulkDebtResp, applyLateFees, LateFeeResp, cancelDebt, Debt, DebtsResp, moneyEntero as money } from '../api';
import { MemberRow } from '../../members/api';
import PersonAvatar from '../../../components/PersonAvatar';
import { listClasses, ClassRow } from '../../classes/api';
import DebtForm from './DebtForm';
import BajasConCargo from './BajasConCargo';
import DesfasadosMes from './DesfasadosMes';
import PaymentForm, { AbonoTarget } from './PaymentForm';
import QuickPay from './QuickPay';
import EmptyState from '../../../components/EmptyState';
import CountUp from '../../../components/CountUp';
import { Skeleton } from '../../../components/Skeleton';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../context/ToastContext';

function monthLabel(m: string | null) {
  if (!m) return '';
  const [y, mm] = m.split('-').map(Number);
  return new Date(y, mm - 1, 1).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' });
}
/** 'YYYY-MM-DD' → '5 jul' (con año si no es el actual; sin desfase de zona). */
function fmtShort(d: string | null) {
  if (!d) return '';
  const [y, mm, dd] = d.split('-').map(Number);
  if (!y || !mm || !dd) return d;
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
  if (y !== new Date().getFullYear()) opts.year = 'numeric';
  return new Date(y, mm - 1, dd).toLocaleDateString('es-MX', opts);
}

type Group = { account_id: number | null; name: string; has_photo: number; total: number; rows: Debt[] };

/** `enfocarDesfasados`: viene de la tarjeta «Desfasados» del Inicio (/finanzas?tab=adeudos&ver=desfasados):
 *  baja hasta la sección «Desfasados de <mes>» y la resalta un momento. */
export default function DebtsPanel({ enfocarDesfasados = false }: { enfocarDesfasados?: boolean } = {}) {
  const { user } = useAuth();
  const desfRef = useRef<HTMLDivElement>(null);
  const [resaltarDesf, setResaltarDesf] = useState(false);
  const [tick, setTick] = useState(0);   // cada reload refresca también la sección de desfasados
  useEffect(() => {
    if (!enfocarDesfasados) return;
    setResaltarDesf(true);
    const t1 = setTimeout(() => desfRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    const t2 = setTimeout(() => setResaltarDesf(false), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [enfocarDesfasados]);
  const toast = useToast();
  const isAdmin = user?.role === 'admin';
  const [resp, setResp] = useState<DebtsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [includeSettled, setIncludeSettled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [showLateFees, setShowLateFees] = useState(false);
  const [abono, setAbono] = useState<AbonoTarget | null>(null);
  const [cancelando, setCancelando] = useState<number | null>(null);   // adeudo que pide motivo
  const [motivoCancel, setMotivoCancel] = useState('');
  const [cancelSaving, setCancelSaving] = useState(false);

  const reload = useCallback(() => {
    const key = `fin-debts:${includeSettled ? 'all' : 'open'}`;
    const cached = readCache<DebtsResp>(key);
    if (cached) { setResp(cached); setLoading(false); } else setLoading(true);
    setTick((n) => n + 1);
    getDebts(includeSettled)
      .then((r) => { setResp(r); writeCache(key, r); })
      .finally(() => setLoading(false));
  }, [includeSettled]);

  useEffect(() => { reload(); }, [reload]);

  const groups = useMemo<Group[]>(() => {
    if (!resp) return [];
    const byAcc = new Map<number | string, Group>();
    for (const d of resp.data) {
      const key = d.contact_id ?? `n-${d.student_name}`;
      if (!byAcc.has(key)) byAcc.set(key, { account_id: d.contact_id, name: d.student_name || 'Alumno', has_photo: d.has_photo, total: 0, rows: [] });
      const g = byAcc.get(key)!;
      g.rows.push(d);
      // Total por alumno = lo que RESTA (neto de abonos), no el monto original.
      if (!d.settled) g.total += Number(d.remaining ?? d.amount) || 0;
    }
    return [...byAcc.values()].sort((a, b) => b.total - a.total);
  }, [resp]);

  /** Cancela (perdona) el adeudo con su motivo. No borra nada: queda en el historial. */
  async function cancelarAdeudo(debtId: number) {
    const motivo = motivoCancel.trim();
    if (!motivo) return;
    setCancelSaving(true);
    try {
      await cancelDebt(debtId, motivo);
      setCancelando(null); setMotivoCancel('');
      toast.success('Adeudo cancelado');
      reload();
    } catch (e: any) { toast.error(e?.message || 'No se pudo cancelar el adeudo'); }
    finally { setCancelSaving(false); }
  }

  async function reopen(d: Debt) {
    try {
      await patchDebtSettled(d.debt_id, false);
      toast.success('Adeudo reabierto');
      reload();
    } catch (e: any) { toast.error(e?.message || 'No se pudo actualizar'); }
  }
  async function onDelete(d: Debt) {
    if (!confirm('¿Eliminar este adeudo manual?')) return;
    try { await deleteDebt(d.debt_id); toast.success('Adeudo eliminado'); reload(); }
    catch (e: any) { toast.error(e?.message || 'No se pudo eliminar'); }
  }

  return (
    <div>
      {/* Las que ya no están pero siguen debiendo: viven al otro lado del filtro
          `is_active = 1` que usan esta lista y «Por cobrar», así que si no se enseñan aquí
          no se enseñan en ningún sitio. Se pinta sola solo cuando hay alguna. */}
      <div className="mb-4"><BajasConCargo /></div>
      {/* Desfasados del mes por separado (Mabel 3-nov-2026). Siguen también en la lista general de
          abajo como hasta ahora: esta sección no cambia totales, solo los junta y dice su estado. */}
      <div className="mb-4"><DesfasadosMes ref={desfRef} refreshKey={tick} resaltar={resaltarDesf} /></div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-content-2">
          <input type="checkbox" checked={includeSettled} onChange={(e) => setIncludeSettled(e.target.checked)}
            className="accent-[rgb(var(--c-accent-solid))]" />
          Mostrar saldados
        </label>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowLateFees(true)} title="Pago tardío de $100 a las mensualidades vencidas (día 4 en adelante)"
              className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
              <AlertTriangle size={16} /> Pagos tardíos
            </button>
            <button onClick={() => setShowGroupForm(true)}
              className="flex items-center gap-1.5 rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-content-2 transition hover:bg-surface-2">
              <Users size={16} /> Deuda a grupo
            </button>
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-1.5 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90">
              <Plus size={16} /> Agregar adeudo
            </button>
          </div>
        )}
      </div>

      {showForm && <DebtForm onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); reload(); }} />}
      {showGroupForm && <GroupDebtForm onClose={() => setShowGroupForm(false)} onCreated={() => { setShowGroupForm(false); reload(); }} />}
      {showLateFees && <LateFeesForm onClose={() => setShowLateFees(false)} onCreated={() => { setShowLateFees(false); reload(); }} />}
      {abono && (
        <PaymentForm abono={abono} onClose={() => setAbono(null)}
          onCreated={() => { setAbono(null); reload(); }} />
      )}

      {/* Resumen */}
      {resp && (
        <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3">
          <div className="flex items-center justify-between gap-2 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:block">
            <div className="text-xs text-content-3">Total por cobrar</div>
            <div className="font-display text-xl font-bold text-amber-600 dark:text-amber-400 sm:text-2xl"><CountUp value={resp.summary.total_open} format={money} /></div>
          </div>
          <div className="flex items-center justify-between gap-2 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:block">
            <div className="text-xs text-content-3">Alumnos con adeudo</div>
            <div className="font-display text-xl font-bold text-content sm:text-2xl"><CountUp value={resp.summary.students_open} /></div>
          </div>
          <div className="flex items-center justify-between gap-2 rounded-2xl border border-line bg-surface p-4 shadow-sm sm:block">
            <div className="text-xs text-content-3">Adeudos abiertos</div>
            <div className="font-display text-xl font-bold text-content sm:text-2xl"><CountUp value={resp.summary.count_open} /></div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-line bg-surface">
          <EmptyState icon={PartyPopper} title="Sin adeudos" subtitle="Nadie tiene pagos pendientes. ¡Todo al corriente!" />
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.account_id ?? g.name} className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              <div className="flex items-center gap-3 border-b border-line bg-surface-2 px-4 py-2.5">
                <PersonAvatar contactId={g.account_id} hasPhoto={g.has_photo && !!g.account_id}
                  name={g.name} size="h-8 w-8" ringClass="fill-surface-2" />
                {g.account_id
                  ? <Link to={`/alumnos/${g.account_id}`} className="flex-1 truncate font-medium text-content hover:text-accent-ink">{g.name}</Link>
                  : <span className="flex-1 truncate font-medium text-content">{g.name}
                      {/* Fiado de vitrina a alguien del equipo: no es alumna, no tiene ficha que abrir */}
                      {!!g.rows[0]?.es_maestro && <span className="ml-2 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-300">maestro / equipo</span>}
                    </span>}
                <span className="font-display font-bold text-amber-600 dark:text-amber-400">{money(g.total)}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {g.rows.map((d) => [(
                    <tr key={d.debt_id} className={`group border-t border-line first:border-0 transition hover:bg-surface-2 ${d.settled ? 'opacity-50' : ''}`}>
                      <td className="w-20 px-4 py-2 text-content-3">{monthLabel(d.month)}</td>
                      <td className="px-4 py-2 text-content">
                        {d.membership_name || d.comment || (d.debt_type === 'membership' ? 'Mensualidad' : 'Adeudo')}
                        {d.source === 'manual' && <span className="ml-2 rounded bg-accent/15 px-1.5 py-0.5 text-[10px] font-medium text-accent-ink">manual</span>}
                        {d.debt_type === 'late_fee' && <span className="ml-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-medium text-red-600 dark:text-red-400">pago tardío</span>}
                        {!!d.settled && <span className="ml-2 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-medium text-emerald-600 dark:text-emerald-400">saldado</span>}
                        {/* "Generada · por quién" SOLO en manuales: en las importadas de Kydemy
                            created_at es la fecha del ETL (29-jun), no la real — mostrarla engaña;
                            su origen ya lo dice la columna del mes. */}
                        {((d.source === 'manual' && d.created_at) || d.due_date) && (
                          <div className="mt-0.5 text-[11px] text-content-3">
                            {d.source === 'manual' && d.created_at && (
                              <>Generada {fmtShort(d.created_at)}{d.created_by_name ? ` · por ${d.created_by_name}` : ''}</>
                            )}
                            {d.due_date && (
                              <span className={d.days_to_due != null && d.days_to_due < 0 && !d.settled ? 'text-red-600 dark:text-red-400 font-medium' : ''}>
                                {d.source === 'manual' && d.created_at ? ' · ' : ''}Vence {fmtShort(d.due_date)}
                                {d.days_to_due != null && ` (${d.days_to_due < 0 ? `vencida hace ${-d.days_to_due} día${d.days_to_due === -1 ? '' : 's'}` : d.days_to_due === 0 ? 'hoy' : `en ${d.days_to_due} día${d.days_to_due === 1 ? '' : 's'}`})`}
                              </span>
                            )}
                          </div>
                        )}
                        {/* COBRO EXPRESS (Carlos 7-ago, comparando con Kydemy): el camino normal
                            —"llegó y paga lo que debe"— es un clic en el método, sin modal. Va
                            SIEMPRE VISIBLE a propósito: los iconos de la derecha sólo aparecen al
                            pasar el ratón, y en tablet de mostrador eso no existe. "Otro…" abre el
                            PaymentForm de siempre para monto distinto, descuento, parcial o fecha. */}
                        {isAdmin && !d.settled && (
                          <div className="mt-1.5 flex flex-wrap items-center gap-2">
                            <QuickPay
                              debtId={d.debt_id}
                              remaining={Number(d.remaining ?? d.amount) || 0}
                              debtType={d.debt_type}
                              month={d.month}
                              concept={d.membership_name || d.comment || (d.debt_type === 'membership' ? 'Mensualidad' : 'Adeudo')}
                              onPaid={reload}
                            />
                            {/* CANCELAR/PERDONAR, AL LADO DE LOS MÉTODOS DE PAGO (Carlos 17-sep):
                                nació en la columna de iconos de la derecha, que vive en `opacity-0
                                group-hover:opacity-100` — en la tablet del mostrador no hay ratón,
                                así que ahí no existía. Va donde ya está en la ficha del alumno:
                                pegado al cobro express, porque son las dos salidas del mismo
                                renglón (o cobra, o se perdona). No borra: pide el motivo y queda
                                en el historial. */}
                            <button onClick={() => { setCancelando(cancelando === d.debt_id ? null : d.debt_id); setMotivoCancel(''); }}
                              title="Cancelar este adeudo (pide el motivo y queda en el historial)"
                              aria-label="Cancelar este adeudo"
                              className="rounded-lg border border-red-500/40 p-2 text-red-600 transition hover:bg-red-500/10 dark:text-red-400">
                              <Ban size={17} />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right font-medium text-content">
                        {money(d.amount)}
                        {/* F2: si hay abonos, mostrar acumulado y saldo */}
                        {Number(d.paid_amount) > 0 && !d.settled && (
                          <div className="mt-0.5 text-[11px] font-normal text-content-3">
                            Abonado {money(d.paid_amount)} · <span className="font-medium text-amber-600 dark:text-amber-400">resta {money(d.remaining)}</span>
                          </div>
                        )}
                      </td>
                      <td className="w-32 px-2 py-2 text-right">
                        {isAdmin && (
                          <div className="hover-reveal flex items-center justify-end gap-1 opacity-0 transition group-hover:opacity-100">
                            {!d.settled && (
                              <button onClick={() => setAbono({
                                debt_id: d.debt_id,
                                remaining: Number(d.remaining ?? d.amount) || 0,
                                concept: d.membership_name || d.comment || (d.debt_type === 'membership' ? 'Mensualidad' : 'Adeudo'),
                                debt_type: d.debt_type, month: d.month,
                                member: d.contact_id ? ({ account_id: d.contact_id, firstname: d.student_name || 'Alumno', lastname: '', has_photo: d.has_photo } as MemberRow) : null,
                              })} title="Abonar (registrar pago a este adeudo)"
                                className="rounded p-1 text-content-3 hover:text-accent-ink">
                                <HandCoins size={15} />
                              </button>
                            )}
                            {/* «Marcar saldado» se QUITÓ el 22-sep-2026 (decisión Carlos): cerraba el adeudo sin
                                pago ni motivo ni rastro. Saldar = Abonar/cobrar o Cancelar. Queda solo «Reabrir». */}
                            {!!d.settled && (
                              <button onClick={() => reopen(d)} title="Reabrir"
                                className="rounded p-1 text-content-3 hover:text-amber-500">
                                <RotateCcw size={15} />
                              </button>
                            )}
                            {/* El de CANCELAR ya no está aquí: se subió junto a los métodos de pago
                                (ver arriba), que es donde se ve sin pasar el ratón. */}
                            {d.source === 'manual' && (
                              <button onClick={() => onDelete(d)} title="Eliminar" className="rounded p-1 text-content-3 hover:text-red-500"><Trash2 size={15} /></button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                    ), cancelando === d.debt_id && (
                      <tr key={`c${d.debt_id}`} className="border-t border-line bg-red-500/[0.06]">
                        <td colSpan={4} className="px-4 py-2.5">
                          {/* 23-sep-2026 (Carlos: «no puedo quitar lo del motivo sin recargar»): con el
                              input a min-w-[16rem] y la tarjeta en overflow-hidden, en la tablet los
                              botones quedaban FUERA, recortados. Ahora todo envuelve y hay ✕ arriba. */}
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="flex w-full items-center justify-between text-sm text-content-2 sm:w-auto">
                              Motivo de la cancelación:
                              <button onClick={() => setCancelando(null)} aria-label="Cerrar" className="text-content-3 hover:text-content sm:hidden"><X size={16} /></button>
                            </span>
                            <input autoFocus value={motivoCancel} onChange={(e) => setMotivoCancel(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter' && motivoCancel.trim()) cancelarAdeudo(d.debt_id); if (e.key === 'Escape') setCancelando(null); }}
                              placeholder="p. ej. se dio de baja en agosto"
                              className="w-full min-w-0 rounded-lg border border-line bg-surface px-2.5 py-1.5 text-sm text-content sm:w-auto sm:flex-1" />
                            <button disabled={!motivoCancel.trim() || cancelSaving} onClick={() => cancelarAdeudo(d.debt_id)}
                              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">
                              Cancelar adeudo
                            </button>
                            <button onClick={() => setCancelando(null)} className="rounded-lg border border-line px-3 py-1.5 text-sm text-content-2">
                              Dejarlo
                            </button>
                          </div>
                        </td>
                      </tr>
                    )])}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** F2 — Deuda GRUPAL (pedido de Karla): misma deuda, mismo monto, a todas las
 *  alumnos activos de una clase (ej. vestuario de festival). Con preview antes de aplicar. */
function GroupDebtForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const [classId, setClassId] = useState('');
  const [amount, setAmount] = useState('');
  const [concept, setConcept] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [preview, setPreview] = useState<BulkDebtResp | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listClasses().then((r) => setClasses(r.data.filter((c) => c.status !== 'archived')));
  }, []);
  // Cualquier cambio invalida el preview (obliga a re-verificar el alcance).
  useEffect(() => { setPreview(null); }, [classId, amount, concept, dueDate]);

  const valid = Number(classId) > 0 && Number(amount) > 0 && concept.trim().length > 0;

  async function doPreview() {
    if (!valid) { toast.error('Elige clase, monto y concepto'); return; }
    setBusy(true);
    try {
      const r = await createBulkDebt({ class_id: Number(classId), amount: Number(amount), concept: concept.trim(), due_date: dueDate || null, dry_run: true });
      setPreview(r);
    } catch (e: any) { toast.error(e?.message || 'No se pudo calcular el alcance'); }
    finally { setBusy(false); }
  }
  async function doCreate() {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await createBulkDebt({ class_id: Number(classId), amount: Number(amount), concept: concept.trim(), due_date: dueDate || null });
      toast.success(`Deuda creada a ${r.count} alumno(s) de ${r.class_name} · total ${money(r.total)}`);
      onCreated();
    } catch (e: any) { toast.error(e?.message || 'No se pudo crear la deuda grupal'); }
    finally { setBusy(false); }
  }

  const inputCls = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
  const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Deuda a un grupo</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className={labelCls}>Clase</label>
            <select value={classId} onChange={(e) => setClassId(e.target.value)} className={inputCls}>
              <option value="">— Elegir clase —</option>
              {classes.map((c) => <option key={c.class_id} value={c.class_id}>{c.name}{c.teacher ? ` · ${c.teacher}` : ''}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Monto por alumno (MXN)</label>
              <input type="number" inputMode="decimal" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Vence (opcional)</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Concepto</label>
            <input value={concept} onChange={(e) => setConcept(e.target.value)} placeholder="Ej. Vestuario festival navideño" className={inputCls} />
          </div>

          {preview && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-content-2">
              Se creará una deuda de <b className="text-content">{money(Number(amount))}</b> a
              {' '}<b className="text-content">{preview.count} alumno(s)</b> de <b className="text-content">{preview.class_name}</b>
              {' '}· total <b className="text-amber-600 dark:text-amber-400">{money(preview.total)}</b>.
              {preview.students && preview.students.length > 0 && (
                <div className="mt-1 max-h-24 overflow-y-auto text-[11px] text-content-3">{preview.students.join(' · ')}</div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            {!preview ? (
              <button disabled={busy || !valid} onClick={doPreview}
                className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                {busy ? 'Calculando…' : 'Ver alcance'}
              </button>
            ) : (
              <button disabled={busy} onClick={doCreate}
                className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line">
                {busy ? 'Creando…' : `Crear ${preview.count} deuda(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>, document.body);
}

/** Multa por pago tardío (Karla 28-jul: $100 desde el día 4; la mensualidad vence el día 3).
 *  Nunca aplica a ciegas: primero se ve a quién le tocaría y cuánto suma. El backend simula
 *  por defecto, así que abrir esta ventana no puede cobrarle a nadie. */
function LateFeesForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const toast = useToast();
  const hoy = new Date();
  const mesActual = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(mesActual);
  const [amount, setAmount] = useState('100');
  const [graceUntil, setGraceUntil] = useState('');
  const [preview, setPreview] = useState<LateFeeResp | null>(null);
  const [excluidos, setExcluidos] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);

  // Cambiar mes o monto invalida el alcance: obliga a volver a mirarlo antes de aplicar.
  useEffect(() => { setPreview(null); setExcluidos([]); }, [month, amount, graceUntil]);

  const seleccionados = useMemo(
    () => (preview?.students || []).filter((s) => !excluidos.includes(s.contact_id)),
    [preview, excluidos]);

  async function doPreview() {
    setBusy(true);
    try {
      const r = await applyLateFees({ month, amount: Number(amount) || undefined, grace_until: graceUntil || null, dry_run: true });
      setPreview(r);
      // Quien está activa, debe y NO tiene ninguna clase casi siempre es una baja que nadie
      // registró (le cancelaron su clase), no una morosa → entra desmarcada. Se puede volver
      // a marcar a mano, pero el default no le cobra a quien ya dejó de venir.
      setExcluidos((r.students || []).filter((s) => s.clases_activas === 0).map((s) => s.contact_id));
    }
    catch (e: any) { toast.error(e?.message || 'No se pudo calcular el alcance'); }
    finally { setBusy(false); }
  }
  async function doApply() {
    if (!preview || !seleccionados.length) return;
    if (!confirm(`Se van a crear ${seleccionados.length} pago(s) tardío(s) de ${money(Number(amount))} · total ${money(seleccionados.length * Number(amount))}. ¿Aplicar?`)) return;
    setBusy(true);
    try {
      const r = await applyLateFees({ month, amount: Number(amount) || undefined, grace_until: graceUntil || null, exclude_contact_ids: excluidos, dry_run: false });
      toast.success(`${r.created} pago(s) tardío(s) creado(s) · total ${money(r.total)}`);
      onCreated();
    } catch (e: any) { toast.error(e?.message || 'No se pudieron aplicar los pagos tardíos'); }
    finally { setBusy(false); }
  }

  const inputCls = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm text-content outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30';
  const labelCls = 'mb-1 block text-xs font-medium uppercase tracking-wide text-content-3';
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true">
      <div className="modal-overlay absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-xl sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold text-content">Pagos tardíos</h3>
          <button onClick={onClose} className="text-content-3 transition hover:text-content"><X size={18} /></button>
        </div>
        <p className="mb-4 text-xs text-content-3">
          La mensualidad vence el día 3. Del día 4 en adelante entra el pago tardío, <b>una sola vez por alumno y mes</b>
          {' '}(no se acumula por día). No se le pone a quien ya pagó completo, ni a los alumnos inactivos.
        </p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Mes</label>
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Pago tardío por alumno</label>
              <input type="number" inputMode="decimal" step="1" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Prórroga hasta (opcional)</label>
            <input type="date" value={graceUntil} onChange={(e) => setGraceUntil(e.target.value)} className={inputCls} />
            <p className="mt-1 text-[11px] text-content-3">
              Si se les dio plazo extra (regreso de vacaciones, etc.), esta fecha manda sobre el día 3:
              el pago tardío aplica a partir del día siguiente.
            </p>
          </div>

          {preview && (
            preview.students && preview.students.length > 0 ? (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2">
                <div className="text-xs text-content-2">
                  Les toca pago tardío a <b className="text-content">{seleccionados.length} alumno(s)</b> ·
                  {' '}total <b className="text-red-600 dark:text-red-400">{money(seleccionados.length * Number(amount))}</b>.
                  {preview.already.count > 0 && (
                    <> Ya tenían pago tardío de este mes: {preview.already.count} ({money(preview.already.total)}).</>
                  )}
                </div>
                <div className="mt-2 max-h-56 space-y-1 overflow-y-auto">
                  {preview.students.map((s) => (
                    <label key={s.contact_id} className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 text-[12px] text-content-2 hover:bg-surface-2">
                      <input type="checkbox" checked={!excluidos.includes(s.contact_id)}
                        onChange={(e) => setExcluidos((prev) => e.target.checked ? prev.filter((id) => id !== s.contact_id) : [...prev, s.contact_id])}
                        className="accent-[rgb(var(--c-accent-solid))]" />
                      <span className="flex-1 truncate text-content">
                        {s.nombre}
                        {s.clases_activas === 0 && (
                          <span className="ml-1.5 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400"
                            title="Activa y con adeudo, pero sin ninguna clase: suele ser una baja no registrada (le cancelaron su clase), no una morosa">
                            sin clases
                          </span>
                        )}
                      </span>
                      {s.plan && <span className="hidden truncate text-content-3 sm:inline">{s.plan}</span>}
                      <span className="text-content-3">debe {money(Number(s.debe) || 0)}</span>
                      <span className="text-red-600 dark:text-red-400">{s.dias_atraso} d</span>
                    </label>
                  ))}
                </div>
                <div className="mt-1 text-[11px] text-content-3">
                  Desmarca a quien no se lo quieras poner.
                  {!!(preview.students || []).some((s) => s.clases_activas === 0) && (
                    <> Las marcadas <b className="text-amber-700 dark:text-amber-400">sin clases</b> vienen
                    desmarcadas: activa + con adeudo + sin clase suele ser una baja que no se registró.</>
                  )}
                  {preview.criterio && <> Cuenta {preview.criterio}.</>}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-xs text-content-2">
                Nadie con mensualidad vencida sin pago tardío en ese mes.
                {preview.already.count > 0 && <> Ya había {preview.already.count} pago(s) tardío(s) ({money(preview.already.total)}).</>}
              </div>
            )
          )}

          {!!preview?.descartados_por_fecha?.length && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[11px] text-content-2">
              <b className="text-content">No se les pone a {preview.descartados_por_fecha.length}</b>: su fecha de vencimiento
              no cae en el mes del adeudo (quedó heredada del lote con que se creó). Conviene corregirlos a mano.
              <div className="mt-1 space-y-0.5 text-content-3">
                {preview.descartados_por_fecha.map((i) => (
                  <div key={i.debt_id}>{i.nombre} · mes {i.month} · vence {fmtShort(i.due_date)} · {money(i.amount)}</div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-1">
            <button onClick={onClose} className="rounded-lg border border-line bg-surface px-4 py-2 text-sm text-content-2 transition hover:bg-surface-2">Cancelar</button>
            {!preview ? (
              <button disabled={busy} onClick={doPreview}
                className="rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:bg-line disabled:text-content-3">
                {busy ? 'Calculando…' : 'Ver a quién le toca'}
              </button>
            ) : (
              <button disabled={busy || !seleccionados.length} onClick={doApply}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-600/90 disabled:bg-line disabled:text-content-3">
                {busy ? 'Aplicando…' : `Aplicar ${seleccionados.length} pago(s) tardío(s)`}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>, document.body);
}
